import { z } from 'zod';
import type { RefinementCtx, ZodEffects, ZodObject, ZodRawShape } from 'zod';

const activeReviewerRequiredMessage = 'Please select an active reviewer.';

const isSameActiveReviewerMessage =
  'The same active reviewer is selected, please select a different reviewer.';

const unrefinedSetActiveReviewerFormSchema = z.object({
  organisationUserId: z.number().optional()
});

export type SetActiveReviewerFormValues = Required<
  z.infer<typeof unrefinedSetActiveReviewerFormSchema>
>;

const organisationUserIdSchema = unrefinedSetActiveReviewerFormSchema.superRefine(
  (formData: SetActiveReviewerFormValues, ctx: RefinementCtx) => {
    // Ensure that an organisation user ID for the new active reviewer is specified on
    // save, even if an ID wasn't loaded. This is so that we can display an
    // error message if the user doesn't select an active reviewer when the form
    // is submitted.
    if (!formData.organisationUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: activeReviewerRequiredMessage,
        path: ['organisationUserId']
      });
    }
  }
);

function getSameActiveReviewerSchema(initialOrganisationUser): ZodEffects<ZodObject<ZodRawShape>> {
  return unrefinedSetActiveReviewerFormSchema.superRefine(
    (formData: SetActiveReviewerFormValues, ctx: RefinementCtx) => {
      if (formData.organisationUserId === initialOrganisationUser) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isSameActiveReviewerMessage,
          path: ['organisationUserId']
        });
      }
    }
  );
}

export function setActiveReviewerFormSchema(
  organisationUserId: number | undefined
): ZodEffects<ZodObject<ZodRawShape>> {
  if (organisationUserId) {
    return getSameActiveReviewerSchema(organisationUserId);
  }

  return organisationUserIdSchema;
}
